import config from "config";
import Pusher from "pusher-js";

export type PusherInstanceId = "kek" | "visal" | "sample" | "default";

export interface PusherConfig {
  key: string;
  cluster: string;
  encrypted?: boolean;
  authEndpoint?: string;
  auth?: {
    headers?: Record<string, string>;
  };
}

class PusherManager {
  private static instance: PusherManager;
  private pusherInstances: Map<PusherInstanceId, Pusher> = new Map();

  private constructor() { }

  static getInstance(): PusherManager {
    if (!PusherManager.instance) {
      PusherManager.instance = new PusherManager();
    }
    return PusherManager.instance;
  }

  private ensureDefaultInstancesInitialized(): void {
    if (this.pusherInstances.size === 0) {
      this.initializeDefaultInstances();
    }
  }

  private initializeDefaultInstances(): void {
    try {
      if (!config.pusher.keys.kek_key) {
        console.warn("KEK pusher key not found in config");
      } else {
        this.createPusherInstance("kek", {
          key: config.pusher.keys.kek_key,
          cluster: config.pusher.cluster,
        });
      }

      if (!config.pusher.keys.visal_key) {
        console.warn("VISAL pusher key not found in config");
      } else {
        this.createPusherInstance("visal", {
          key: config.pusher.keys.visal_key,
          cluster: config.pusher.cluster,
        });

        this.createPusherInstance("default", {
          key: config.pusher.keys.visal_key,
          cluster: config.pusher.cluster,
        });
      }

      if (!config.pusher.keys.sample) {
        console.warn("SAMPLE pusher key not found in config");
      } else {
        this.createPusherInstance("sample", {
          key: config.pusher.keys.sample,
          cluster: "mt1",
        });

        this.createPusherInstance("default", {
          key: config.pusher.keys.sample,
          cluster: config.pusher.cluster,
        });
      }

      if (!config.pusher.keys.visal_key && !config.pusher.keys.kek_key && !config.pusher.keys.sample) {
        throw new Error("No pusher keys found in configuration");
      }
    } catch (error) {
      console.error("Failed to initialize default pusher instances:", error);
      throw error;
    }
  }

  createPusherInstance(
    instanceId: PusherInstanceId,
    pusherConfig: PusherConfig
  ): Pusher {
    if (this.pusherInstances.has(instanceId)) {
      return this.pusherInstances.get(instanceId)!;
    }

    if (!pusherConfig.key) {
      throw new Error(`Pusher key is required for instance '${instanceId}'`);
    }

    if (!pusherConfig.cluster) {
      throw new Error(
        `Pusher cluster is required for instance '${instanceId}'`
      );
    }

    try {
      const pusherOptions: any = {
        cluster: pusherConfig.cluster,
      };

      // Only add optional properties if they have values
      if (pusherConfig.encrypted !== undefined) {
        pusherOptions.encrypted = pusherConfig.encrypted;
      }

      if (pusherConfig.authEndpoint) {
        pusherOptions.authEndpoint = pusherConfig.authEndpoint;
      }

      if (pusherConfig.auth) {
        pusherOptions.auth = pusherConfig.auth;
      }

      // console.log(`Creating pusher instance '${instanceId}' with options:`, {
      //     key: pusherConfig.key.substring(0, 8) + '***',
      //     ...pusherOptions
      // });

      const pusher = new Pusher(pusherConfig.key, pusherOptions);

      this.pusherInstances.set(instanceId, pusher);
      return pusher;
    } catch (error) {
      console.error(`Failed to create pusher instance '${instanceId}':`, error);
      throw error;
    }
  }

  getPusherInstance(instanceId: PusherInstanceId = "default"): Pusher {
    this.ensureDefaultInstancesInitialized();
    const instance = this.pusherInstances.get(instanceId);
    if (!instance) {
      throw new Error(
        `Pusher instance '${instanceId}' not found. Available instances: ${Array.from(
          this.pusherInstances.keys()
        ).join(", ")}`
      );
    }
    return instance;
  }

  getAllInstances(): Map<PusherInstanceId, Pusher> {
    return new Map(this.pusherInstances);
  }

  removePusherInstance(instanceId: PusherInstanceId): void {
    const pusher = this.pusherInstances.get(instanceId);
    if (pusher) {
      pusher.disconnect();
      this.pusherInstances.delete(instanceId);
    }
  }

  cleanup(): void {
    this.pusherInstances.forEach((pusher) => {
      pusher.disconnect();
    });
    this.pusherInstances.clear();
  }
}

export const pusherManager = PusherManager.getInstance();
